import { createSlice } from "@reduxjs/toolkit";
import {
  createSchoolThunk,
  getSchoolByIdThunk,
  getSchoolsThunk,
  toggleSchoolActiveThunk,
  updateSchoolThunk,
} from "./schools.thunks";

const initialState = {
  list: [],
  loading: false,
  error: null,
  page: 1,
  pageSize: 10,
  totalPages: 1,
  count: 0,
  next: null,
  previous: null,
  maxPageReached: 1,
  search: "",

  selectedSchool: null,
  selectedSchoolLoading: false,
  selectedSchoolError: null,
  viewModalOpen: false,

  formModalOpen: false,
  formMode: "create", // "create" | "edit"
  formLoading: false,
  formError: null,

  confirmModalOpen: false,
  toggleSchoolId: null,
  toggling: false,
};

const schoolsSlice = createSlice({
  name: "schools",
  initialState,
  reducers: {
    clearSchoolsError: (state) => {
      state.error = null;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setSearch: (state, action) => {
      state.search = action.payload;
      state.page = 1; // Reset a página 1 al buscar
    },
    openViewModal: (state) => {
      state.viewModalOpen = true;
    },
    closeViewModal: (state) => {
      state.viewModalOpen = false;
      state.selectedSchool = null;
    },
    openFormModal: (state, action) => {
      state.formModalOpen = true;
      state.formMode = action.payload?.mode || "create";
      if (action.payload?.school) {
        state.selectedSchool = action.payload.school;
      }
      state.formError = null;
    },
    closeFormModal: (state) => {
      state.formModalOpen = false;
      state.selectedSchool = null;
      state.formError = null;
    },
    openConfirmToggle: (state, action) => {
      state.confirmModalOpen = true;
      state.toggleSchoolId = action.payload.id;
    },
    closeConfirmToggle: (state) => {
      state.confirmModalOpen = false;
      state.toggleSchoolId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET ALL
      .addCase(getSchoolsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSchoolsThunk.fulfilled, (state, action) => {
        const { results, count, next, previous } = action.payload;

        state.loading = false;
        state.list = results;
        state.count = count;
        state.next = next;
        state.previous = previous;

        state.maxPageReached = Math.max(state.maxPageReached, state.page);
      })
      .addCase(getSchoolsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createSchoolThunk.pending, (state) => {
        state.formLoading = true;
        state.formError = null;
      })
      .addCase(createSchoolThunk.fulfilled, (state, action) => {
        state.formLoading = false;
        state.list.unshift(action.payload);
        state.formModalOpen = false;
      })
      .addCase(createSchoolThunk.rejected, (state, action) => {
        state.formLoading = false;
        state.formError = action.payload;
      })

      // GET BY ID
      .addCase(getSchoolByIdThunk.pending, (state) => {
        state.selectedSchoolLoading = true;
        state.selectedSchoolError = null;
      })
      .addCase(getSchoolByIdThunk.fulfilled, (state, action) => {
        state.selectedSchoolLoading = false;
        state.selectedSchool = action.payload;
      })
      .addCase(getSchoolByIdThunk.rejected, (state, action) => {
        state.selectedSchoolLoading = false;
        state.selectedSchoolError = action.payload;
      })

      // UPDATE
      .addCase(updateSchoolThunk.pending, (state) => {
        state.formLoading = true;
        state.formError = null;
      })
      .addCase(updateSchoolThunk.fulfilled, (state, action) => {
        state.formLoading = false;
        const index = state.list.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        state.formModalOpen = false;
      })
      .addCase(updateSchoolThunk.rejected, (state, action) => {
        state.formLoading = false;
        state.formError = action.payload;
      })

      // TOGGLE ACTIVE
      .addCase(toggleSchoolActiveThunk.pending, (state) => {
        state.toggling = true;
      })
      .addCase(toggleSchoolActiveThunk.fulfilled, (state, action) => {
        state.toggling = false;

        const id = state.toggleSchoolId;
        const school = state.list.find((s) => s.id === id);
        if (school) {
          school.is_active = action.payload.is_active;
        }

        if (state.selectedSchool?.id === id) {
          state.selectedSchool.is_active = action.payload.is_active;
        }

        state.confirmModalOpen = false;
      })
      .addCase(toggleSchoolActiveThunk.rejected, (state) => {
        state.toggling = false;
      });
  },
});

export const {
  clearSchoolsError,
  setPage,
  setSearch,
  openViewModal,
  closeViewModal,
  openFormModal,
  closeFormModal,
  openConfirmToggle,
  closeConfirmToggle,
} = schoolsSlice.actions;
export default schoolsSlice.reducer;
